import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'
import _uniqueId from 'lodash/uniqueId'
import { List } from 'immutable'

import { api } from './api'
import * as actions from './actions'
import * as selectors from './selectors'
import * as GateSelectors from 'app/modules/gates/selectors'
import * as ConversationSelectors from 'app/modules/conversation/selectors'
import * as SocketConstants from 'app/modules/socket/constants'
import { ConversationStatusEnum } from 'constants/ConversationStatusConstants'

const VALID_RECEIVE_BULK_UPDATE_STATUSES = ['PENDING', 'FAILED', 'SUCCESS']

const handleBulkUpdateSocketEvent = (action$, store) =>
  action$
    .ofType(SocketConstants.JOIN_CHANNEL_PENDING)
    .pluck('payload')
    .filter((payload) => payload.clusterName === SocketConstants.SocketCluster.MESSAGES)
    .filter((payload) => payload.channelName === SocketConstants.SocketChannel.ORG_PRIVATE)
    .switchMap(({ channel, leaveChannel$ }) => {
      return Observable.create((observer) => {
        channel.on('conversation-bulk-updated', (payload) => {
          const {
            updaterId,
            updateType,
            updateInfo: conversationDiffs,
            requestId: updateRequestId,
            orgId,
            inboxId,
            error,
          } = payload

          if (error) {
            return observer.next(actions.receiveBulkUpdate({ status: 'FAILED', updateRequestId, }))
          }

          observer.next(actions.receiveBulkUpdate({
            status: 'SUCCESS',
            updaterId,
            conversationDiffs: conversationDiffs.map(conversationDiff => {
              return {
                previousStatus: conversationDiff.previousStatus,
                previousParticipantIds: List(conversationDiff.previousParticipantIds),
                currentStatus: conversationDiff.currentStatus,
                currentParticipantIds: List(conversationDiff.currentParticipantIds),
                conversationId: conversationDiff.conversationId,
              }
            }),
            conversationIds: List(conversationDiffs.map(conversationDiff => conversationDiff.conversationId)),
            updateRequestId,
            inboxId,
          }))
        })
      }).takeUntil(leaveChannel$)
    })

const requestBulkUpdate = (action$, { getState }) =>
  action$
    .ofType(actions.requestBulkUpdate.toString())
    .pluck('payload')
    // NOTE: Backend currenly only supports bulk close.
    .filter(({ conversationStatus }) => conversationStatus === ConversationStatusEnum.CLOSED.key)
    .mergeMap(({ updateRequestId, inboxId, conversationIds }) => {
      if (GateSelectors.hasDemoOrg(getState())) {
        return Observable.timer(100) // Artificial delay
          .map(() => actions.requestBulkUpdateSuccess({ inboxId, tempUpdateRequestId: updateRequestId, updateRequestId: _uniqueId('server_generated_update_request_') }))
          .startWith(actions.requestBulkUpdatePending({ updateRequestId }))
      } else {
        return api
          .requestBulkClose({ inboxId, conversationIds })
          .map(({
              response: {
                requestId: serverGeneratedUpdateRequestId
              }
            }) => {
            return actions.requestBulkUpdateSuccess({
              inboxId,
              tempUpdateRequestId: updateRequestId,
              updateRequestId: serverGeneratedUpdateRequestId,
            })
          })
          .startWith(actions.requestBulkUpdatePending({ updateRequestId }))
          .catch((error) => Observable.of(actions.requestBulkUpdateFailed({ error, updateRequestId })))
      }
    })

const requestBulkUpdateSuccess = (action$) =>
  action$
    .ofType(actions.requestBulkUpdateSuccess.toString())
    .pluck('payload')
    .map(({ updateRequestId }) => actions.receiveBulkUpdate({ updateRequestId, status: 'PENDING' }))

const receiveBulkUpdate = (action$) => {
  return action$
    .ofType(actions.receiveBulkUpdate.toString())
    .pluck('payload')
    .filter(({ status }) => VALID_RECEIVE_BULK_UPDATE_STATUSES.includes(status))
    .map((payload) => {
      const { updateRequestId, status } = payload

      switch (status) {
        case 'PENDING':
          return actions.receiveBulkUpdatePending({ updateRequestId })
        case 'FAILED':
          return actions.receiveBulkUpdateFailed({ updateRequestId })
        case 'SUCCESS':
          return actions.receiveBulkUpdateSuccess(payload)
      }
    })
}

const receiveBulkUpdatePending = (action$, { getState }) => {
  const participantIdsByConversationIdSelector = ConversationSelectors.makeParticipantIdsByConversationId()
  const conversationStatusByConversationIdSelector = ConversationSelectors.makeConversationStatusByConversationId()

  return action$
    .ofType(actions.receiveBulkUpdatePending.toString())
    .pluck('payload')
    .filter(() => GateSelectors.hasDemoOrg(getState()))
    .mergeMap(({ updateRequestId }) => {
      const state = getState()

      const bulkUpdateRequest = selectors.bulkUpdateRequest(state, { updateRequestId })

      const participantIdsByConversationId = participantIdsByConversationIdSelector(state, {
        conversationIds: bulkUpdateRequest.get('conversationIds'),
      })

      const conversationStatusByConversationId = conversationStatusByConversationIdSelector(state, {
        conversationIds: bulkUpdateRequest.get('conversationIds'),
      })

      const successPayload = {
        inboxId: bulkUpdateRequest.get('inboxId'),
        updaterId: bulkUpdateRequest.get('updaterId'),
        updateRequestId: bulkUpdateRequest.get('id'),
        conversationDiffs: bulkUpdateRequest.get('conversationIds').map(conversationId => {
          const participantIds = participantIdsByConversationId.get(conversationId)

          return {
            conversationId,
            currentStatus: bulkUpdateRequest.get('conversationStatus'),
            previousStatus: conversationStatusByConversationId.get(conversationId),
            currentParticipantIds: participantIds,
            previousParticipantIds: participantIds,
          }
        })
      }

      return Observable.timer(500) // Artificial delay
        .map(() => actions.receiveBulkUpdateSuccess(successPayload))
    })
}


export const epic = combineEpics(
  handleBulkUpdateSocketEvent,
  requestBulkUpdate,
  requestBulkUpdateSuccess,
  receiveBulkUpdate,
  receiveBulkUpdatePending,
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/conversation/bulkUpdate/epic.js